<template>
  <v-form ref="form" v-model="valid" @submit.prevent="onSubmit" class="payment-form">
    <!-- 1. Card Information - first (primary focus) -->
    <div class="payment-form-section">
      <div class="payment-form-section-header">
        <v-icon small color="primary">mdi-credit-card-outline</v-icon>
        <span>Card details</span>
      </div>
      <v-row v-if="tokenizationKey" class="collectjs-fields">
        <v-col cols="12">
          <label class="payment-field-label">Card number</label>
          <div id="ccnumber" class="collectjs-field"></div>
        </v-col>
        <v-col cols="6">
          <label class="payment-field-label">Expiration</label>
          <div id="ccexp" class="collectjs-field"></div>
        </v-col>
        <v-col cols="6">
          <label class="payment-field-label">CVV</label>
          <div id="cvv" class="collectjs-field"></div>
        </v-col>
      </v-row>
      <p v-if="tokenizationKey" class="payment-security-note">
        <v-icon x-small>mdi-lock</v-icon>
        Secured by our payment partner
      </p>
    </div>

    <!-- 2. Billing Information -->
    <div class="payment-form-section">
      <div class="payment-form-section-header">
        <v-icon small color="primary">mdi-map-marker-outline</v-icon>
        <span>Billing address</span>
      </div>
      <v-row>
        <v-col cols="6">
          <v-text-field
            v-model="billingInfo.firstName"
            label="First name"
            :rules="[rules.required]"
            outlined
            dense
            hide-details="auto"
            class="payment-field"
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model="billingInfo.lastName"
            label="Last name"
            :rules="[rules.required]"
            outlined
            dense
            hide-details="auto"
            class="payment-field"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="billingInfo.address"
            label="Address"
            :rules="[rules.required]"
            outlined
            dense
            hide-details="auto"
            class="payment-field"
          />
        </v-col>
        <v-col cols="5">
          <v-text-field
            v-model="billingInfo.city"
            label="City"
            :rules="[rules.required]"
            outlined
            dense
            hide-details="auto"
            class="payment-field"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="billingInfo.state"
            label="State"
            :rules="[rules.required]"
            outlined
            dense
            hide-details="auto"
            class="payment-field"
          />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model="billingInfo.zip"
            label="ZIP"
            :rules="[rules.required]"
            outlined
            dense
            hide-details="auto"
            class="payment-field"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="billingInfo.country"
            label="Country"
            :rules="[rules.required]"
            outlined
            dense
            hide-details="auto"
            class="payment-field"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="billingInfo.phone"
            label="Phone (optional)"
            outlined
            dense
            hide-details
            class="payment-field"
          />
        </v-col>
        <v-col cols="12" v-if="mode === 'pay'">
          <v-checkbox
            v-model="saveForAutoPay"
            label="Save this card for future Auto Pay"
            class="mt-0 payment-checkbox"
            hide-details
          />
        </v-col>
        <!--
          No opt-out here. Saving a card always turns Auto Pay on: the dashboard's
          /add-card sets autoPay.enabled unconditionally, and this form never sent the
          old checkbox anyway — leaving it unticked still enabled Auto Pay. A control
          that cannot change the outcome is worse than a plain statement of it.
        -->
        <v-col cols="12" v-if="mode === 'add-card'">
          <p class="payment-autopay-note">
            <v-icon x-small>mdi-autorenew</v-icon>
            Saving this card turns on Auto Pay for future payments.
          </p>
        </v-col>
      </v-row>
    </div>

    <!-- When no tokenization key configured -->
    <v-alert v-if="!configLoaded && !tokenizationKey" type="info" dense class="mb-4">
      Loading payment form...
    </v-alert>
    <v-alert v-else-if="configLoaded && !tokenizationKey" type="warning" dense class="mb-4">
      Payment form not configured. Set MIDPAY_TOKENIZATION_KEY in the server environment (Azure).
    </v-alert>

    <!-- Actions -->
    <div class="payment-form-actions">
      <v-btn text @click="$emit('cancel')" class="mr-2">Cancel</v-btn>
      <v-btn
        id="payButton"
        color="primary"
        type="submit"
        :loading="loading || submitting"
        :disabled="!tokenizationKey || submitting"
        depressed
        class="payment-submit-btn"
      >
        {{ mode === 'pay' ? 'Pay Now' : 'Save Card' }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PaymentForm',
  props: {
    mode: {
      type: String,
      default: 'pay',
      validator: (v) => ['pay', 'add-card'].indexOf(v) >= 0
    },
    submitting: {
      type: Boolean,
      default: false
    },
    amount: {
      type: Number,
      default: null
    },
    user: {
      type: Object,
      default: () => ({})
    },
    profile: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      valid: false,
      loading: false,
      saveForAutoPay: false,
      tokenizationKey: '',
      configLoaded: false,
      billingInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: 'TX',
        zip: '',
        country: 'US'
      },
      rules: {
        required: (v) => !!v || 'Required',
        email: (v) => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return !v || pattern.test(v) || 'Invalid email';
        }
      }
    };
  },
  watch: {
    user: {
      immediate: true,
      handler(val) {
        if (val) {
          this.billingInfo.firstName = val.firstname || val.firstName || this.billingInfo.firstName;
          this.billingInfo.lastName = val.lastname || val.lastName || this.billingInfo.lastName;
          this.billingInfo.email = val.email || this.billingInfo.email;
        }
      }
    },
    profile: {
      immediate: true,
      handler(val) {
        if (val) {
          this.billingInfo.firstName = val.firstName || this.billingInfo.firstName;
          this.billingInfo.lastName = val.lastName || this.billingInfo.lastName;
          this.billingInfo.email = val.email || this.billingInfo.email;
          this.billingInfo.address = val.address || this.billingInfo.address;
          this.billingInfo.city = val.city || this.billingInfo.city;
          this.billingInfo.state = val.state || this.billingInfo.state;
          this.billingInfo.zip = val.zip || this.billingInfo.zip;
          this.billingInfo.country = val.country || this.billingInfo.country;
          this.billingInfo.phone = val.phone || this.billingInfo.phone;
        }
      }
    }
  },
  async mounted() {
    // Fetch tokenization key from API (runtime - works in Azure)
    // Fallback to build-time env for local dev
    try {
      const res = await axios.get('/api/member-payments/config');
      if (res.data && res.data.tokenizationKey) {
        this.tokenizationKey = res.data.tokenizationKey;
      }
    } catch (err) {
      console.warn('Could not fetch payment config:', err && err.message);
    }
    this.configLoaded = true;
    if (!this.tokenizationKey) {
      this.tokenizationKey = process.env.VUE_APP_MIDPAY_TOKENIZATION_KEY || '';
    }
    if (this.tokenizationKey) {
      this.loadCollectJS();
    }
  },
  methods: {
    loadCollectJS() {
      const scriptUrl = process.env.VUE_APP_MIDPAY_COLLECT_URL || 'https://www.midpaygateway.com/token/Collect.js';
      const key = this.tokenizationKey;
      const existing = document.querySelector('script[src*="Collect.js"]');
      if (!existing) {
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.setAttribute('data-tokenization-key', key);
        script.setAttribute('data-variant', 'inline');
        document.head.appendChild(script);
        script.onload = () => this.configureCollectJS();
      } else if (window.CollectJS) {
        this.$nextTick(() => this.configureCollectJS());
      }
    },
    configureCollectJS() {
      if (!window.CollectJS) return;
      const self = this;
      window.CollectJS.configure({
        paymentSelector: '#payButton',
        callback: async function collectJSCallback(response) {
          try {
            if (self.submitting) return;
            const valid = await self.$refs.form.validate();
            if (!valid) return;
            self.loading = true;
            const billing = { ...self.billingInfo };
            if (!billing.email && self.user && self.user.email) {
              billing.email = self.user.email;
            }
            const payload = {
              paymentToken: response.token,
              billingInfo: billing
            };
            // Only the pay flow offers a choice: there the checkbox decides whether the
            // card is vaulted. Adding a card always enables Auto Pay, so there is nothing
            // to report back for that mode.
            if (self.mode === 'pay') {
              payload.amount = self.amount;
              payload.saveForAutoPay = self.saveForAutoPay;
            }
            self.$emit('success', payload);
          } catch (err) {
            console.error('CollectJS callback error:', err);
            self.$emit('error', err.message || 'Payment failed');
          } finally {
            self.loading = false;
          }
        }
      });
    },
    async onSubmit() {
      if (!this.tokenizationKey) {
        this.$emit('error', 'Payment form not configured.');
        return;
      }
      await this.$refs.form.validate();
    }
  }
};
</script>

<style scoped>
.payment-form {
  padding: 0;
}
.payment-form-section {
  margin-bottom: 24px;
}
.payment-form-section:last-of-type {
  margin-bottom: 0;
}
.payment-form-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.payment-field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 6px;
}
.payment-autopay-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.payment-security-note {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 8px 0 0 0;
  display: flex;
  align-items: center;
  gap: 4px;
}
.payment-form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.payment-submit-btn {
  min-width: 140px;
  text-transform: none;
  font-weight: 600;
}
.payment-checkbox >>> .v-label {
  font-size: 0.875rem;
}
/* Collect.js injects iframes into these divs */
.collectjs-field {
  min-height: 44px;
  position: relative;
  z-index: 1;
  pointer-events: auto;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 8px;
  padding: 10px 14px;
  background: #fafafa;
  overflow: visible;
  transition: border-color 0.2s, background 0.2s;
}
.collectjs-field:focus-within,
.collectjs-field:hover {
  border-color: var(--v-primary-base, #1976d2);
  background: #fff;
}
.collectjs-field ::v-deep iframe {
  pointer-events: auto !important;
  min-height: 28px !important;
  width: 100% !important;
}
</style>
